/*!
 * GentlePup theme — motion.js
 * Copyright © 2026 Murilo Scigliano. All rights reserved.
 * Proprietary. See LICENSE. dev@murilo.design
 */
/**
 * The motion layer — reveals, a scroll-scrubbed statement, tile tilt.
 *
 * Everything here is decoration. The page renders complete without it:
 * the stylesheet hides [data-reveal] only under a .motion-ready class,
 * and this file is the only thing that sets that class — after checking
 * IntersectionObserver exists and reduced motion is not requested.
 * No-JS, old-browser and reduced-motion visitors never see a hidden
 * state, because the state that would hide things is never switched on.
 *
 * Three modules, all lerp-on-rAF like SmoothScroll:
 *
 *   - Reveals — an IntersectionObserver adds .is-revealed and stops
 *     watching. Stagger containers get a --reveal-index per child; the
 *     delay lives in CSS, so the script writes one number and never
 *     touches timing.
 *   - StatementScrub — the statement section's progress through the
 *     viewport maps to a count of lit words, and the same progress
 *     drives --scrub on the inline lozenges so they grow as the
 *     sentence reads out.
 *   - TileTilt — pointer-follow on the category tiles, a few degrees
 *     at most. Touch has no pointer to follow, so it is never attached.
 *
 * Motion is a preference, not a setting, so a mid-session change to
 * reduced motion tears everything down: removing .motion-ready returns
 * every element to its full, static rendering without the script
 * having to clean up a single inline style.
 */

// Wrapped in an IIFE because classic deferred scripts share one global
// lexical scope: a top-level const or function here (LERP, sync, …) is
// a parse-time redeclaration against smooth-scroll.js and would kill
// the whole file before a line of it ran.
(() => {
/** Shared damping, the same feel as smooth-scroll.js. */
const LERP = 0.09;
/** Below this, the remaining distance is invisible; snap and stop. */
const SETTLE = 0.004;
/** Tilt is a nod, not a flip. */
const MAX_TILT_DEG = 4;
/** How long a reveal will wait for its own images before going anyway. */
const REVEAL_HOLD_MS = 400;
/** The image drifts against the tilt for a little parallax. */
const MAX_DRIFT_PX = 8;

class Reveals {
  constructor() {
    this.controller = new AbortController();

    /* Images are fetched BEFORE the reveal is allowed to fire.
       
       The reveal observer triggers at -28%, deep into view, and the
       images inside are loading="lazy" — so the same moment that
       revealed a panel was the first moment its photograph started
       downloading. settle() then waited REVEAL_HOLD_MS, gave up, and
       animated an empty box with the image arriving after it. On the
       contact page that is a 1200x1500 portrait, and the pop was
       impossible to miss.

       This observer runs 600px ahead of the viewport and switches those
       images to eager, so the bytes are already in flight by the time
       the reveal trigger is reached. It only ever promotes images that
       are about to be shown, so nothing off-screen is fetched early. */
    this.preloader = new IntersectionObserver(this.handlePreload, {
      rootMargin: "600px 0px 600px 0px",
      threshold: 0,
    });

    this.observer = new IntersectionObserver(this.handleIntersect, {
      // Trigger once the element is well into view, not as its edge
      // first crosses the fold — an entrance that starts too early is
      // over before the visitor is watching.
      rootMargin: "0px 0px -28% 0px",
      threshold: 0,
    });
    // An element at the very foot of the page can never reach 28% into
    // the window, so the deep trigger alone would hide it forever.
    // Reaching the bottom of the page reveals whatever is still waiting.
    window.addEventListener("scroll", this.revealAtPageEnd, {
      signal: this.controller.signal,
      passive: true,
    });
    this.revealAtPageEnd();
    this.scan(document);
    this.revealWhatIsAlreadyVisible();
  }

  /**
   * Everything on screen at load reveals together, in one pass.
   *
   * The observer deliberately waits until an element is 28% into the
   * window before firing, which is right for something being scrolled
   * to — the entrance should not be over before the visitor arrives.
   * But it is wrong for what is already in front of them: on a tall
   * desktop screen a large part of the page is visible at load, and
   * anything under that line stayed invisible until a scroll moved it,
   * so a visitor met a page with holes in it and had to scroll to
   * assemble what they could already see.
   *
   * Anything within the viewport when the page settles is therefore
   * revealed straight away. Below-fold content is untouched and still
   * cascades as it is reached.
   *
   * Two frames, not one: the first lets layout settle after the scan,
   * the second measures against the settled boxes. Measuring in the
   * same frame as the scan reads stale rectangles for anything the
   * fonts or images have not sized yet.
   */
  revealWhatIsAlreadyVisible = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const fold = window.innerHeight;
        for (const element of document.querySelectorAll("[data-reveal]:not(.is-revealed)")) {
          const box = element.getBoundingClientRect();
          // Any part of it on screen counts. An element straddling the
          // fold is one a visitor can already see the top of, and
          // holding it back is the hole this exists to close.
          if (box.top >= fold || box.bottom <= 0) continue;
          element.classList.add("is-revealed");
          this.observer.unobserve(element);
        }
      });
    });
  };

  revealAtPageEnd = () => {
    const bottom = window.innerHeight + window.scrollY;
    if (bottom < document.documentElement.scrollHeight - 4) return;
    for (const element of document.querySelectorAll("[data-reveal]:not(.is-revealed)")) {
      element.classList.add("is-revealed");
      this.observer.unobserve(element);
    }
  };

  // Also called with the swapped root on shopify:section:load, so
  // sections the theme editor re-renders do not stay hidden. Re-observing
  // an element is a no-op, and .is-revealed nodes keep their end state.
  scan(root) {
    // A stagger container numbers its children; CSS turns the number
    // into a transition-delay. Keeping the count here means the
    // stylesheet never has to know how many items a merchant added.
    for (const container of root.querySelectorAll("[data-reveal-stagger]")) {
      container.querySelectorAll("[data-reveal]").forEach((item, index) => {
        item.style.setProperty("--reveal-index", index);
      });
    }

    // A tall container restarts its stagger per screenful rather than
    // counting across the whole thing.
    //
    // Two failure modes to avoid, both seen on this site:
    //
    //   Running 0..n — the eighteenth card carried index 17 and waited
    //   1.26s after its trigger, so the bottom of a collection grid sat
    //   blank while the visitor scrolled past it.
    //
    //   Collapsing every index to 0 — the first fix here — removed the
    //   cascade altogether. Forty FAQ items all landed with no delay,
    //   which reads as a block appearing followed by ragged arrivals,
    //   not as a stagger.
    //
    // Grouping by vertical position gives both: items that appear
    // together cascade together, and the count restarts often enough
    // that no item ever waits long. A grid groups by row; a single
    // column groups by however many rows fit on screen.
    for (const container of root.querySelectorAll("[data-reveal-stagger]")) {
      // A container can ask for the whole cascade from one trigger even
      // when it is taller than the window — the FAQ does, because the
      // page IS the list and a single sweep down it reads better than
      // the list re-cascading every screenful. Owner call, 2026-08-22.
      if (container.hasAttribute("data-reveal-sweep")) continue;
      if (!this.isTallerThanViewport(container)) continue;
      // Descendant, not ":scope >", to match the numbering pass above.
      // The FAQ nests its forty questions inside .faq__items rather than
      // hanging them off the stagger container, so a direct-child
      // selector matched nothing there.
      const items = [...container.querySelectorAll("[data-reveal]")];
      if (items.length < 2) continue;
      this.groupByBand(items);
    }

    for (const element of root.querySelectorAll("[data-reveal]")) {
      // Staggered items are usually watched through their container: a
      // short row would otherwise fire its left cards before the right
      // ones crossed the trigger, and the cascade would arrive in
      // pieces. The container firing reveals them all at once, and the
      // per-item delay does the staggering.
      //
      // That breaks down once the container is taller than the window.
      // An 18-product collection grid fired all eighteen cards from one
      // trigger, so the delay cap (14 beats x 90ms) plus the 800ms
      // entrance left the bottom rows blank for over two seconds while
      // the visitor was already scrolling past them — the page read as
      // laggy and half-broken. Audited 2026-08-22 on /collections/best-sellers.
      //
      // So a container that cannot fit on screen hands its children back
      // to the observer: each row reveals as it is reached, which is
      // both faster to first paint and impossible to out-scroll.
      // data-reveal-sweep opts out of that fallback as well as the
      // banding above: a container carrying it keeps ONE trigger however
      // tall it is. The product buy column needs this — the price, the
      // options, the bundle and the button are one offer, and on a short
      // desktop window the lower half sat under the observer's -28%
      // bottom margin at load, so the shopper met a half-rendered offer
      // and had to scroll to see what they were buying.
      const container = element.closest("[data-reveal-stagger]");
      const sweeps = container?.hasAttribute("data-reveal-sweep");
      const target =
        container && (sweeps || !this.isTallerThanViewport(container)) ? container : element;
      this.observer.observe(target);
      this.preloader.observe(target);
    }
  }

  /** Measured, not guessed: a grid's height depends on its content. */
  isTallerThanViewport(element) {
    return element.getBoundingClientRect().height > window.innerHeight;
  }

  /**
   * Numbers items so the cascade restarts once per screenful.
   *
   * Items are banded by their vertical position: everything within one
   * viewport height of the band's start shares that band and is
   * numbered 0,1,2... inside it. A three-across grid therefore cascades
   * a few rows at a time, and a long single-column list cascades the
   * handful of items actually visible together.
   *
   * Positions come from offsetTop rather than the computed
   * grid-template-columns, which may be an auto-fit repeat() the
   * browser has already resolved for us.
   */
  groupByBand(items) {
    const band = Math.max(window.innerHeight * 0.9, 320);
    let bandTop = items[0].offsetTop;
    let index = 0;

    for (const item of items) {
      if (item.offsetTop - bandTop > band) {
        bandTop = item.offsetTop;
        index = 0;
      }
      item.style.setProperty("--reveal-index", index);
      index += 1;
    }
  }

  destroy() {
    this.observer.disconnect();
    this.preloader.disconnect();
    this.controller.abort();
  }

  handlePreload = (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      this.preloader.unobserve(entry.target);
      for (const img of entry.target.querySelectorAll('img[loading="lazy"]')) {
        img.loading = "eager";
        // fetchPriority is a hint, not a guarantee, and is ignored by
        // browsers that do not support it — which is the correct
        // fallback rather than a polyfill.
        img.fetchPriority = "high";
      }
    }
  };

  handleIntersect = (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      // Revealed is revealed — there is no re-hiding on the way back up.
      this.observer.unobserve(entry.target);
      this.settle(entry.target);
    }
  };

  /* The entrance used to race the lazy fetch. The same intersection that
     reveals a panel is what starts its images loading, so on anything
     slower than cable the entrance finished before the photograph had —
     the animation played on an empty box and the image popped in after
     it. The reveal now waits for the images it is about to show, but
     never past REVEAL_HOLD_MS: a genuinely slow image arrives into a
     visible section rather than holding it hostage. decode() doubles as
     a nudge — the element is in view, so the bytes are wanted now. */
  settle(target) {
    const apply = () => {
      if (target.hasAttribute("data-reveal-stagger")) {
        // The container carries the class too: rules that key off the
        // group's arrival (the trust-strip rules) match a class on the
        // element itself, not a :has() the browser forgets to
        // recalculate when classes land in one JS frame.
        target.classList.add("is-revealed");
        for (const item of target.querySelectorAll("[data-reveal]")) {
          item.classList.add("is-revealed");
        }
      } else {
        target.classList.add("is-revealed");
      }
    };

    const pending = Array.from(target.querySelectorAll("img")).filter(
      (img) => !img.complete
    );
    if (!pending.length) {
      apply();
      return;
    }

    Promise.race([
      Promise.allSettled(pending.map((img) => img.decode())),
      new Promise((resolve) => setTimeout(resolve, REVEAL_HOLD_MS)),
    ]).then(apply);
  }
}

class StatementScrub {
  constructor() {
    this.frame = null;
    this.tracks = [];
    this.controller = new AbortController();

    const { signal } = this.controller;
    window.addEventListener("scroll", this.start, { signal, passive: true });
    window.addEventListener("resize", this.start, { signal, passive: true });

    this.refresh();
    this.start();
  }

  // The homepage statement and any pitch statement ([data-scrub]) play
  // the same word-by-word read. Re-queried on refresh, because the
  // theme editor can swap sections without a page load.
  refresh() {
    this.tracks = Array.from(document.querySelectorAll(".statement, [data-scrub]")).map((element) => ({
      element,
      words: element.querySelectorAll(".statement__word"),
      media: element.querySelectorAll(".statement__media"),
      // The first tick snaps to the real progress rather than lerping up
      // from 0: a statement in the initial viewport would otherwise take
      // a second to catch up, repainting dimmed words as they light.
      current: 0,
      snapped: false,
    }));
  }

  destroy() {
    cancelAnimationFrame(this.frame);
    this.controller.abort();
  }

  start = () => {
    if (this.tracks.length && this.frame === null) {
      this.frame = requestAnimationFrame(this.tick);
    }
  };

  tick = () => {
    // One layout read per track, then the writes — never interleaved,
    // or every frame pays for a reflow.
    const viewport = window.innerHeight;
    let allSettled = true;

    for (const track of this.tracks) {
      const rect = track.element.getBoundingClientRect();
      // 0 when the section's top enters at the foot of the window, 1
      // when its foot leaves at the top.
      const progress = Math.min(1, Math.max(0, (viewport - rect.top) / (viewport + rect.height)));

      if (!track.snapped) {
        track.snapped = true;
        track.current = progress;
      }

      track.current += (progress - track.current) * LERP;
      const settled = Math.abs(progress - track.current) < SETTLE;
      if (settled) {
        track.current = progress;
      } else {
        allSettled = false;
      }

      // The middle of the journey carries the sentence: doubling the
      // progress puts full-lit and full-size at centred, rather than
      // 1.4x, which only completed as the section scrolled away.
      const journey = Math.min(1, track.current * 2);
      const lit = Math.round(journey * track.words.length);
      track.words.forEach((word, index) => {
        word.classList.toggle("is-lit", index < lit);
      });

      const scrub = journey.toFixed(3);
      for (const media of track.media) {
        media.style.setProperty("--scrub", scrub);
      }
    }

    this.frame = allSettled ? null : requestAnimationFrame(this.tick);
  };
}

class TileTilt {
  constructor() {
    this.controller = new AbortController();
    this.states = new Map();

    const { signal } = this.controller;
    // Cached tile boxes go stale when the page moves; the next
    // pointermove re-reads rather than measuring a shifted tile.
    window.addEventListener("scroll", this.invalidateRects, { signal, passive: true });
    window.addEventListener("resize", this.invalidateRects, { signal, passive: true });

    this.attach(document);
  }

  // Attach any tiles under root that are not already wired up. Called
  // again with the swapped root on shopify:section:load, where the new
  // DOM carries no listeners.
  attach(root) {
    const { signal } = this.controller;
    for (const tile of root.querySelectorAll(".category-tile")) {
      if (this.states.has(tile)) continue;

      const state = {
        tile,
        rect: null,
        frame: null,
        targetRX: 0,
        targetRY: 0,
        targetX: 0,
        targetY: 0,
        rx: 0,
        ry: 0,
        x: 0,
        y: 0,
      };
      // The tile box is read on entry and cached for the whole hover —
      // one layout read per hover instead of one per pointermove.
      tile.addEventListener(
        "pointerenter",
        () => {
          state.rect = tile.getBoundingClientRect();
        },
        { signal, passive: true }
      );
      tile.addEventListener("pointermove", (event) => this.handleMove(state, event), {
        signal,
        passive: true,
      });
      tile.addEventListener("pointerleave", () => this.handleLeave(state), { signal, passive: true });
      this.states.set(tile, state);
    }
  }

  destroy() {
    this.controller.abort();
    for (const state of this.states.values()) {
      cancelAnimationFrame(state.frame);
      // Inline properties are the script's footprint; leave the tile as
      // the stylesheet drew it.
      state.tile.style.removeProperty("--tilt-rx");
      state.tile.style.removeProperty("--tilt-ry");
      state.tile.style.removeProperty("--tilt-x");
      state.tile.style.removeProperty("--tilt-y");
    }
    this.states.clear();
  }

  invalidateRects = () => {
    for (const state of this.states.values()) {
      state.rect = null;
    }
  };

  handleMove = (state, event) => {
    // pointerenter normally primes the cache; a move can still arrive
    // first in synthetic event sequences, so read on demand.
    if (!state.rect) state.rect = state.tile.getBoundingClientRect();
    const rect = state.rect;

    // Pointer position within the tile, -.5 to .5 on each axis.
    const px = (event.clientX - rect.left) / rect.width - 0.5;
    const py = (event.clientY - rect.top) / rect.height - 0.5;

    // The tile tips toward the pointer; the image drifts the other way,
    // which is what reads as depth rather than a rotating card.
    state.targetRY = px * MAX_TILT_DEG;
    state.targetRX = -py * MAX_TILT_DEG;
    state.targetX = -px * MAX_DRIFT_PX;
    state.targetY = -py * MAX_DRIFT_PX;
    this.start(state);
  };

  handleLeave = (state) => {
    state.targetRX = 0;
    state.targetRY = 0;
    state.targetX = 0;
    state.targetY = 0;
    this.start(state);
  };

  start(state) {
    if (state.frame === null) state.frame = requestAnimationFrame(() => this.tick(state));
  }

  tick = (state) => {
    state.rx += (state.targetRX - state.rx) * LERP;
    state.ry += (state.targetRY - state.ry) * LERP;
    state.x += (state.targetX - state.x) * LERP;
    state.y += (state.targetY - state.y) * LERP;

    const settled =
      Math.abs(state.targetRX - state.rx) < SETTLE &&
      Math.abs(state.targetRY - state.ry) < SETTLE &&
      Math.abs(state.targetX - state.x) < SETTLE &&
      Math.abs(state.targetY - state.y) < SETTLE;

    if (settled) {
      state.rx = state.targetRX;
      state.ry = state.targetRY;
      state.x = state.targetX;
      state.y = state.targetY;
    }

    // Custom properties, not a transform: the transform itself stays in
    // the stylesheet, so this loop only ever writes four numbers.
    state.tile.style.setProperty("--tilt-rx", `${state.rx.toFixed(2)}deg`);
    state.tile.style.setProperty("--tilt-ry", `${state.ry.toFixed(2)}deg`);
    state.tile.style.setProperty("--tilt-x", `${state.x.toFixed(1)}px`);
    state.tile.style.setProperty("--tilt-y", `${state.y.toFixed(1)}px`);

    state.frame = settled ? null : requestAnimationFrame(() => this.tick(state));
  };
}

class CursorMarquee {
  /**
   * The badge that trails the pointer while it is over a
   * [data-cursor-marquee-text] card and scrolls the card's pitch
   * through it. It only ever exists inside a card: the moment the
   * pointer leaves, the badge closes where it is and the trail stops
   * — it never chases the mouse across the page.
   */
  constructor() {
    this.cursor = document.querySelector("[data-cursor-marquee-status]");
    if (!this.cursor) return;
    this.targets = this.cursor.querySelectorAll("[data-cursor-marquee-text-target]");
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.frame = null;
    this.x = 0;
    this.y = 0;
    this.targetX = 0;
    this.targetY = 0;
    this.active = null;
    this.pauseTimer = null;

    window.addEventListener("pointermove", this.handleMove, { signal, passive: true });
    window.addEventListener("scroll", this.handleScroll, { signal, passive: true });

    // Hidden until the script is ready: the status attribute is what
    // lets the card become visible at all.
    this.cursor.setAttribute("data-cursor-marquee-status", "not-active");
  }

  destroy() {
    cancelAnimationFrame(this.frame);
    clearTimeout(this.pauseTimer);
    this.controller.abort();
    this.cursor?.removeAttribute("data-cursor-marquee-status");
    // The scroll state is an inline footprint like the badge position:
    // leave the spans as the stylesheet drew them, not stuck mid-play.
    this.targets.forEach((target) => {
      target.style.animationPlayState = "";
    });
  }

  handleMove = (event) => {
    this.targetX = event.clientX;
    this.targetY = event.clientY;
    this.checkTarget();
    // The trail only runs while there is somewhere to trail to.
    if (this.active) this.start();
  };

  handleScroll = () => {
    // The pointer is still while the page moves under it — the element
    // at the point can change without a pointermove firing.
    this.checkTarget();
  };

  start() {
    if (this.frame === null) this.frame = requestAnimationFrame(this.tick);
  }

  tick = () => {
    this.x += (this.targetX - this.x) * LERP;
    this.y += (this.targetY - this.y) * LERP;

    const settled =
      Math.abs(this.targetX - this.x) < SETTLE * 100 &&
      Math.abs(this.targetY - this.y) < SETTLE * 100;

    if (settled) {
      this.x = this.targetX;
      this.y = this.targetY;
    }

    this.cursor.style.left = `${this.x.toFixed(1)}px`;
    this.cursor.style.top = `${this.y.toFixed(1)}px`;
    this.frame = settled ? null : requestAnimationFrame(this.tick);
  };

  checkTarget() {
    const candidate = document.elementFromPoint(this.targetX, this.targetY)?.closest("[data-cursor-marquee-text]");
    // An empty pitch is no pitch: the attribute can render empty when a
    // card has no copy for the badge, and opening it would show a badge
    // with nothing to say.
    const hit = candidate && candidate.getAttribute("data-cursor-marquee-text").trim() ? candidate : null;
    if (hit === this.active) return;
    if (this.active) this.pauseLater();
    if (hit) this.playFor(hit);
  }

  playFor(element) {
    clearTimeout(this.pauseTimer);
    const text = element.getAttribute("data-cursor-marquee-text") || "";
    // Duration scales with length, so any pitch scrolls at the same pace.
    const seconds = (text.length || 1) / 5;
    this.targets.forEach((target) => {
      target.textContent = text;
      target.style.animationDuration = `${seconds}s`;
      target.style.animationPlayState = "running";
    });
    // Open where the pointer is, not wherever the badge last rested —
    // a fly-in from across the page is what read as harsh.
    this.x = this.targetX;
    this.y = this.targetY;
    this.cursor.style.left = `${this.x.toFixed(1)}px`;
    this.cursor.style.top = `${this.y.toFixed(1)}px`;
    this.cursor.setAttribute("data-cursor-marquee-status", "active");
    this.active = element;
    this.start();
  }

  pauseLater() {
    this.cursor.setAttribute("data-cursor-marquee-status", "not-active");
    clearTimeout(this.pauseTimer);
    // The scroll keeps rolling while the card folds away, so the exit
    // reads as one motion rather than a stutter and a close.
    this.pauseTimer = setTimeout(() => {
      this.targets.forEach((target) => {
        target.style.animationPlayState = "paused";
      });
    }, 400);
    this.active = null;
  }
}

/**
 * Reduced motion is a request not to do this at all, and without
 * IntersectionObserver the reveals could never fire — either one and
 * the class that unlocks the hidden states stays off, leaving the page
 * exactly as it rendered.
 *
 * theme.liquid runs the same two checks inline in the head so the class
 * is present at first paint; this is the authority afterwards, owning
 * teardown and preference changes.
 */
const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
const finePointer = window.matchMedia("(pointer: fine)");
const canObserve = "IntersectionObserver" in window;

let reveals = null;
let scrub = null;
let tilt = null;
let marquee = null;

function sync() {
  const motionWanted = canObserve && !reducedMotion.matches;

  document.documentElement.classList.toggle("motion-ready", motionWanted);

  if (motionWanted && !reveals) {
    reveals = new Reveals();
    scrub = new StatementScrub();
  } else if (!motionWanted && reveals) {
    reveals.destroy();
    scrub.destroy();
    reveals = null;
    scrub = null;
  }

  // Tilt and the cursor badge follow a pointer, so they are
  // fine-pointer affordances on top of the motion gate — touch devices
  // keep the plain tiles and no badge.
  const pointerWanted = motionWanted && finePointer.matches;

  if (pointerWanted && !tilt) {
    tilt = new TileTilt();
  } else if (!pointerWanted && tilt) {
    tilt.destroy();
    tilt = null;
  }

  if (pointerWanted && !marquee) {
    marquee = new CursorMarquee();
  } else if (!pointerWanted && marquee) {
    marquee.destroy();
    marquee = null;
  }
}

// The theme editor swaps a section's DOM without a page load. New
// [data-reveal] nodes would arrive unobserved and sit hidden under the
// persisting .motion-ready class, so each loaded section gets re-scanned.
// Only the editor fires this event; on the storefront it never runs.
document.addEventListener("shopify:section:load", (event) => {
  reveals?.scan(event.target);
  scrub?.refresh();
  tilt?.attach(event.target);
});

reducedMotion.addEventListener("change", sync);
finePointer.addEventListener("change", sync);
sync();
})();


/* Shopify's video_tag writes a fallback <img> inside every <video> with
   no alt at all. These posters are decoration behind the section's own
   copy, so an explicit empty alt marks them decorative for assistive
   tech and audits alike. */
document.querySelectorAll("video img:not([alt])").forEach((img) => img.setAttribute("alt", ""));


/* Poster-first video. The clips shipped with autoplay, which made the
   browser pull megabytes of MP4 before first paint — the hero clip
   alone was the LCP. The markup now carries no autoplay and no
   preload; the poster paints as the LCP image, and each video begins
   streaming only when the clip is near the viewport. A clip already in
   view when this deferred script runs is part of the first screen, so
   it starts at DOM-ready rather than holding for every image on the
   page; clips further down wait for the load event before arming.
   Reduced-motion visitors keep the stills. */
(() => {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  const clips = document.querySelectorAll(".hero__media video, .problem-solution__media video, .product__gallery video, .pitch__media video, .product-story__media video");
  if (!clips.length) return;
  const begin = (video) => {
    if (video.dataset.begun) return;
    // A clip under an A/B test waits: the server always renders the
    // control, so starting it here would play the wrong video for a
    // beat before hero-video.js swaps in the drawn one. That script
    // clears the hold and starts it once the source is right.
    if (video.closest("[data-video-hold]")) return;
    video.dataset.begun = "1";
    // A container can name a lighter rendition for narrow viewports
    // (the hero ships the lowest MP4 as data-video-mobile). The swap has
    // to happen before the first play: <source> children outrank the src
    // attribute, so they go and the attribute takes over.
    const mobile = video.closest("[data-video-mobile]");
    const desktop = video.closest("[data-video-desktop]");
    if (mobile && window.matchMedia("(max-width: 767px)").matches) {
      video.innerHTML = "";
      video.src = mobile.dataset.videoMobile;
    } else if (desktop) {
      // video_tag ships one <source>, Shopify's top rendition (1080p at
      // 7.2 Mbps on the pitch clips); the wrapper names the 720p one.
      video.innerHTML = "";
      video.src = desktop.dataset.videoDesktop;
    } else {
      // Every clip here is a muted looping background — the HD-720
      // 4.5Mbps rendition the browser picks by default is 8MB of
      // indistinguishable pixels. Shopify names the rendition in the
      // URL ("...HD-720p-4.5Mbps..."), so pick the lightest MP4 at or
      // above 360p and let the poster cover the switch. Unparseable
      // or single-rendition videos keep the default.
      const mp4s = Array.from(video.querySelectorAll("source"))
        .map((s) => ({ src: s.src, h: Number((s.src.match(/(\d{3,4})p-/) || [])[1]) || 0 }))
        .filter((s) => s.h >= 360 && /\.mp4/.test(s.src))
        .sort((a, b) => a.h - b.h);
      if (mp4s.length > 1 && mp4s[0].h < mp4s[mp4s.length - 1].h) {
        video.innerHTML = "";
        video.src = mp4s[0].src;
      }
    }
    // The hero's responsive poster <img> covers the video until there is
    // a real frame on screen; only then may it fade.
    video.addEventListener("playing", () => {
      video.closest(".hero__media")?.classList.add("is-playing");
    }, { once: true });
    video.preload = "auto";
    video.play().catch(() => {});
  };
  // A clip on the first screen still yields to the LCP: its poster is
  // the largest paint, and a 4MB stream started at DOM-ready shares the
  // connection with it. Wait for the poster's bytes, then start.
  clips.forEach((v) => {
    const r = v.getBoundingClientRect();
    if (!(r.top < window.innerHeight && r.bottom > 0)) return;
    const poster =
      v.closest(".hero__media")?.querySelector("img.hero__poster") ||
      v.closest(".product__gallery")?.querySelector("img.product__image");
    if (poster && !poster.complete) {
      poster.addEventListener("load", () => begin(v), { once: true });
      poster.addEventListener("error", () => begin(v), { once: true });
    } else {
      begin(v);
    }
  });
  const arm = () => {
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) { begin(e.target); io.unobserve(e.target); } }),
      { rootMargin: "200px" },
    );
    clips.forEach((v) => { if (!v.dataset.begun) io.observe(v); });
  };
  if (document.readyState === "complete") arm();
  else window.addEventListener("load", arm, { once: true });
})();
